import {
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
} from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

export interface CreosAsset {
  id: number
  created_by: number
  created_by_username?: string | null
  vertical_slug: string
  vertical_name: string
  language: string
  slide_count: number
  aspect_ratio: string
  asset_type: string
  status: string
  buyer_prompt: string
  prompt_used?: unknown
  seed?: number | null
  s3_urls: { key: string; url: string; download_url?: string }[]
  reference_spaces_keys?: string[] | null
  error_message?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface CreosAssetListResponse {
  items: CreosAsset[]
  total: number
}

export interface CreosListParams {
  status?: string
  vertical_slug?: string
  limit?: number
  offset?: number
}

type RefetchInterval<T> =
  | number
  | false
  | ((query: Query<T, Error>) => number | false | undefined)

export const CREOS_QUERY_KEYS = {
  all: ["creos"] as const,
  list: (params: CreosListParams) => ["creos", "list", params] as const,
  detail: (id: number) => ["creos", "detail", id] as const,
}

function buildListUrl(params: CreosListParams): string {
  const qs = new URLSearchParams()
  if (params.status) qs.set("status", params.status)
  if (params.vertical_slug) qs.set("vertical_slug", params.vertical_slug)
  if (params.limit != null) qs.set("limit", String(params.limit))
  if (params.offset != null) qs.set("offset", String(params.offset))
  const q = qs.toString()
  return q ? `/api/creos/assets?${q}` : "/api/creos/assets"
}

export function useCreosAssets(
  params: CreosListParams = {},
  options: { refetchInterval?: RefetchInterval<CreosAssetListResponse> } = {},
) {
  return useQuery<CreosAssetListResponse>({
    queryKey: CREOS_QUERY_KEYS.list(params),
    queryFn: () => apiFetch<CreosAssetListResponse>(buildListUrl(params)),
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export function useCreosAsset(
  id: number,
  options: {
    enabled?: boolean
    refetchInterval?: RefetchInterval<CreosAsset>
  } = {},
) {
  return useQuery<CreosAsset>({
    queryKey: CREOS_QUERY_KEYS.detail(id),
    queryFn: () => apiFetch<CreosAsset>(`/api/creos/assets/${id}`),
    enabled: options.enabled ?? id > 0,
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export interface CreateCreosAssetInput {
  buyer_prompt: string
  vertical_slug: string
  vertical_name: string
  language: string
  aspect_ratio: string
  slide_count: number
  files: File[]
}

export function useCreateCreosAsset() {
  const qc = useQueryClient()
  return useMutation<CreosAsset, Error, CreateCreosAssetInput>({
    mutationFn: async (input) => {
      const fd = new FormData()
      fd.append("buyer_prompt", input.buyer_prompt)
      fd.append("vertical_slug", input.vertical_slug)
      fd.append("vertical_name", input.vertical_name)
      fd.append("language", input.language)
      fd.append("aspect_ratio", input.aspect_ratio)
      fd.append("slide_count", String(input.slide_count))
      for (const file of input.files) {
        fd.append("files", file)
      }
      return apiFetch<CreosAsset>("/api/creos/assets", {
        method: "POST",
        body: fd,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CREOS_QUERY_KEYS.all })
    },
  })
}

export interface CreosUniqueFile {
  key: string
  filename: string
  content_type: string
  url: string
  download_url: string
}

export interface CreosUniqueJob {
  id: number
  public_id: string
  batch_id?: string | null
  created_by: number
  created_by_username?: string | null
  media_kind: string
  file_count: number
  status: string
  source_filenames: string[]
  s3_urls: CreosUniqueFile[]
  error_message?: string | null
  created_at?: string | null
  updated_at?: string | null
  download_url?: string | null
  batch_download_url?: string | null
  duration_sec?: number | null
  /** e.g. campaign_form when launched from Create Campaign */
  source?: string | null
}

export interface CreosUniqueJobListResponse {
  items: CreosUniqueJob[]
  total: number
}

export interface CreosUniqueBatch {
  batch_id: string | null
  solo_public_id: string | null
  status: string
  job_count: number
  completed_count: number
  failed_count: number
  pending_count: number
  processing_count: number
  media_kinds: string[]
  source_preview: string[]
  created_by: number
  created_by_username?: string | null
  created_at?: string | null
  updated_at?: string | null
  download_url?: string | null
  duration_sec?: number | null
  source?: string | null
}

export interface CreosUniqueBatchDetail extends CreosUniqueBatch {
  jobs: CreosUniqueJob[]
}

export interface CreosUniqueBatchListResponse {
  items: CreosUniqueBatch[]
  total: number
}

export interface CreosUniqueListParams {
  status?: string
  q?: string
  mine?: boolean
  limit?: number
  offset?: number
}

export const CREOS_UNIQUE_QUERY_KEYS = {
  all: ["creos-uniquify"] as const,
  list: (params: CreosUniqueListParams) =>
    ["creos-uniquify", "list", params] as const,
  batches: (params: CreosUniqueListParams) =>
    ["creos-uniquify", "batches", params] as const,
  detail: (id: string) => ["creos-uniquify", "detail", id] as const,
  batchDetail: (id: string) => ["creos-uniquify", "batch", id] as const,
}

function buildUniqueQuery(params: CreosUniqueListParams): string {
  const qs = new URLSearchParams()
  if (params.status) qs.set("status", params.status)
  if (params.q?.trim()) qs.set("q", params.q.trim())
  if (params.mine) qs.set("mine", "true")
  if (params.limit != null) qs.set("limit", String(params.limit))
  if (params.offset != null) qs.set("offset", String(params.offset))
  const q = qs.toString()
  return q ? `?${q}` : ""
}

function buildUniqueListUrl(params: CreosUniqueListParams): string {
  return `/api/creos/uniquify${buildUniqueQuery(params)}`
}

function buildUniqueBatchesUrl(params: CreosUniqueListParams): string {
  return `/api/creos/uniquify/batches${buildUniqueQuery(params)}`
}

export function useCreosUniqueJobs(
  params: CreosUniqueListParams = {},
  options: { refetchInterval?: RefetchInterval<CreosUniqueJobListResponse> } = {},
) {
  return useQuery<CreosUniqueJobListResponse>({
    queryKey: CREOS_UNIQUE_QUERY_KEYS.list(params),
    queryFn: () =>
      apiFetch<CreosUniqueJobListResponse>(buildUniqueListUrl(params)),
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export function useCreosUniqueBatches(
  params: CreosUniqueListParams = {},
  options: {
    refetchInterval?: RefetchInterval<CreosUniqueBatchListResponse>
  } = {},
) {
  return useQuery<CreosUniqueBatchListResponse>({
    queryKey: CREOS_UNIQUE_QUERY_KEYS.batches(params),
    queryFn: () =>
      apiFetch<CreosUniqueBatchListResponse>(buildUniqueBatchesUrl(params)),
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export function useCreosUniqueJob(
  id: string,
  options: {
    enabled?: boolean
    refetchInterval?: RefetchInterval<CreosUniqueJob>
  } = {},
) {
  return useQuery<CreosUniqueJob>({
    queryKey: CREOS_UNIQUE_QUERY_KEYS.detail(id),
    queryFn: () => apiFetch<CreosUniqueJob>(`/api/creos/uniquify/${id}`),
    enabled: options.enabled ?? Boolean(id),
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export function useCreosUniqueBatch(
  batchId: string,
  options: {
    enabled?: boolean
    refetchInterval?: RefetchInterval<CreosUniqueBatchDetail>
  } = {},
) {
  return useQuery<CreosUniqueBatchDetail>({
    queryKey: CREOS_UNIQUE_QUERY_KEYS.batchDetail(batchId),
    queryFn: () =>
      apiFetch<CreosUniqueBatchDetail>(
        `/api/creos/uniquify/batches/${batchId}`,
      ),
    enabled: options.enabled ?? Boolean(batchId),
    refetchInterval: options.refetchInterval,
    retry: false,
  })
}

export function useCreateCreosUniqueJob() {
  const qc = useQueryClient()
  return useMutation<CreosUniqueJob, Error, { files: File[]; batchId?: string | null }>({
    mutationFn: async ({ files, batchId }) => {
      const fd = new FormData()
      for (const file of files) {
        fd.append("files", file)
      }
      if (batchId) {
        fd.append("batch_id", batchId)
      }
      return apiFetch<CreosUniqueJob>("/api/creos/uniquify", {
        method: "POST",
        body: fd,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CREOS_UNIQUE_QUERY_KEYS.all })
    },
  })
}

export function useRestartCreosUniqueJob() {
  const qc = useQueryClient()
  return useMutation<CreosUniqueJob, Error, string>({
    mutationFn: (id) =>
      apiFetch<CreosUniqueJob>(`/api/creos/uniquify/${id}/restart`, {
        method: "POST",
      }),
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: CREOS_UNIQUE_QUERY_KEYS.all })
      qc.setQueryData(CREOS_UNIQUE_QUERY_KEYS.detail(job.public_id), job)
    },
  })
}
